// DEPRECATED: This file is kept for backward compatibility during migration
// All Supabase calls should be migrated to use the API client in api.ts
// This file re-exports types and provides a compatibility layer

// Re-export types from api.ts
export type {
  UserRole,
  Profile,
  Job,
  Application,
  Certification,
  InsurancePolicy,
  Notification,
  Message,
  TaxDocument,
} from './api';

export {
  maskTaxId,
  formatTaxId,
  validateTaxId,
} from './api';

// Compatibility layer - converts Supabase-style queries to API calls
import { api } from './api';

// Create a compatibility object that mimics Supabase's API
class SupabaseCompat {
  // Simulate Supabase's .from() query builder
  from(table: string) {
    return {
      select: (columns: string = '*') => ({
        eq: (column: string, value: any) => ({
          maybeSingle: async () => {
            const params: Record<string, any> = {};
            params[column] = value;
            try {
              const data = await api.get(`/${table}`, params);
              return { data, error: null };
            } catch (error: any) {
              return { data: null, error };
            }
          },
          single: async () => {
            const params: Record<string, any> = {};
            params[column] = value;
            try {
              const data = await api.get(`/${table}`, params);
              return { data, error: null };
            } catch (error: any) {
              return { data: null, error };
            }
          },
          gte: (col: string, val: number) => ({
            order: (orderCol: string, options?: { ascending?: boolean }) => ({
              limit: (limitValue: number) => {
                const execute = async () => {
                  const params: Record<string, any> = {};
                  params[column] = value;
                  params[`${col}_min`] = val;
                  params.limit = limitValue;
                  try {
                    const data = await api.get(`/${table}`, params);
                    return { data, error: null };
                  } catch (error: any) {
                    return { data: null, error };
                  }
                };
                return {
                  then: execute,
                  catch: (handler: (error: any) => any) => execute().catch(handler),
                };
              },
              then: async () => {
                const params: Record<string, any> = {};
                params[column] = value;
                params[`${col}_min`] = val;
                try {
                  const data = await api.get(`/${table}`, params);
                  return { data, error: null };
                } catch (error: any) {
                  return { data: null, error };
                }
              },
            }),
          }),
          order: (col: string, options?: { ascending?: boolean }) => ({
            limit: (limitValue: number) => {
              const execute = async () => {
                const params: Record<string, any> = {};
                params[column] = value;
                params.limit = limitValue;
                try {
                  const data = await api.get(`/${table}`, params);
                  return { data, error: null };
                } catch (error: any) {
                  return { data: null, error };
                }
              };
              return {
                then: execute,
                catch: (handler: (error: any) => any) => execute().catch(handler),
              };
            },
            then: async () => {
              const params: Record<string, any> = {};
              params[column] = value;
              try {
                const data = await api.get(`/${table}`, params);
                return { data, error: null };
              } catch (error: any) {
                return { data: null, error };
              }
            },
          }),
        }),
        ilike: (column: string, pattern: string) => ({
          eq: (col: string, val: any) => ({
            order: (col: string, options?: { ascending?: boolean }) => ({
              limit: (limitValue: number) => {
                const execute = async () => {
                  const params: Record<string, any> = {};
                  params[col] = val;
                  params[`${column}_search`] = pattern.replace(/%/g, '');
                  params.limit = limitValue;
                  try {
                    const data = await api.get(`/${table}`, params);
                    return { data, error: null };
                  } catch (error: any) {
                    return { data: null, error };
                  }
                };
                return {
                  then: execute,
                  catch: (handler: (error: any) => any) => execute().catch(handler),
                };
              },
              then: async (callback?: (query: any) => any) => {
                const params: Record<string, any> = {};
                params[col] = val;
                params[`${column}_search`] = pattern.replace(/%/g, '');
                try {
                  const data = await api.get(`/${table}`, params);
                  return { data, error: null };
                } catch (error: any) {
                  return { data: null, error };
                }
              },
            }),
          }),
          order: (col: string, options?: { ascending?: boolean }) => ({
            limit: (limitValue: number) => {
              const execute = async () => {
                const params: Record<string, any> = {};
                params[`${column}_search`] = pattern.replace(/%/g, '');
                params.limit = limitValue;
                try {
                  const data = await api.get(`/${table}`, params);
                  return { data, error: null };
                } catch (error: any) {
                  return { data: null, error };
                }
              };
              return {
                then: execute,
                catch: (handler: (error: any) => any) => execute().catch(handler),
              };
            },
            then: async (callback?: (query: any) => any) => {
              const params: Record<string, any> = {};
              params[`${column}_search`] = pattern.replace(/%/g, '');
              try {
                const data = await api.get(`/${table}`, params);
                return { data, error: null };
              } catch (error: any) {
                return { data: null, error };
              }
            },
          }),
        }),
        order: (col: string, options?: { ascending?: boolean }) => ({
          limit: (limitValue: number) => {
            const execute = async () => {
              try {
                const params: Record<string, any> = { limit: limitValue };
                const data = await api.get(`/${table}`, params);
                return { data, error: null };
              } catch (error: any) {
                return { data: null, error };
              }
            };
            return {
              then: execute,
              catch: (handler: (error: any) => any) => execute().catch(handler),
            };
          },
          then: async () => {
            try {
              const data = await api.get(`/${table}`);
              return { data, error: null };
            } catch (error: any) {
              return { data: null, error };
            }
          },
        }),
      }),
      insert: async (data: any) => {
        try {
          const result = await api.post(`/${table}`, data);
          return { data: result, error: null };
        } catch (error: any) {
          return { data: null, error };
        }
      },
      update: (data: any) => ({
        eq: async (column: string, value: any) => {
          try {
            // For profiles table, use /profiles/me endpoint instead of /profiles/{id}
            const endpoint = table === 'profiles' ? '/profiles/me' : `/${table}/${value}`;
            const result = await api.patch(endpoint, data);
            return { data: result, error: null };
          } catch (error: any) {
            return { data: null, error };
          }
        },
      }),
      delete: () => ({
        eq: async (column: string, value: any) => {
          try {
            await api.delete(`/${table}/${value}`);
            return { data: null, error: null };
          } catch (error: any) {
            return { data: null, error };
          }
        },
      }),
    };
  }

  // Storage compatibility (simplified)
  storage = {
    from: (bucket: string) => ({
      upload: async (path: string, file: File, options?: any) => {
        try {
          const result = await api.uploadFile(`/storage/${bucket}/upload`, file, { path });
          return { data: { path }, error: null };
        } catch (error: any) {
          return { data: null, error };
        }
      },
      getPublicUrl: (path: string) => ({
        data: {
          publicUrl: `${import.meta.env.VITE_API_URL || 'https://api.vendorboom.com/api/v1'}/storage/${bucket}/${path}`,
        },
      }),
      remove: async (paths: string[]) => {
        try {
          await api.post(`/storage/${bucket}/delete`, { paths });
          return { data: null, error: null };
        } catch (error: any) {
          return { data: null, error };
        }
      },
    }),
  };

  // Auth compatibility (minimal - should use authApi directly)
  auth = {
    getSession: async () => {
      try {
        const user = await api.get('/auth/me');
        return { data: { session: user ? { user } : null }, error: null };
      } catch {
        return { data: { session: null }, error: null };
      }
    },
    onAuthStateChange: (callback: (event: string, session: any) => void) => {
      // Polling-based implementation (can be improved with WebSockets if API supports it)
      const interval = setInterval(async () => {
        try {
          const user = await api.get('/auth/me');
          callback('SIGNED_IN', user ? { user } : null);
        } catch {
          callback('SIGNED_OUT', null);
        }
      }, 5000);

      return {
        data: {
          subscription: {
            unsubscribe: () => clearInterval(interval),
          },
        },
      };
    },
    signUp: async (credentials: { email: string; password: string }) => {
      // This is a simplified version - should use authApi.signUp with role
      throw new Error('Use authApi.signUp() instead - this method requires role parameter');
    },
    signInWithPassword: async (credentials: { email: string; password: string }) => {
      // This is a simplified version - should use authApi.signIn
      throw new Error('Use authApi.signIn() instead');
    },
    signOut: async () => {
      // This is a simplified version - should use authApi.signOut
      throw new Error('Use authApi.signOut() instead');
    },
  };
}

export const supabase = new SupabaseCompat() as any;
